import { useEffect, useMemo, useRef } from 'react';

/**
 * Returns a stable function that delays invoking the latest `callback`
 * until `delayMs` of inactivity -- several rapid calls (ticking a run of
 * checkboxes, holding down a stepper) collapse into one. Always calls the
 * most recent `callback`/args, never a stale closure from an earlier render.
 */
export function useDebouncedCallback<A extends unknown[]>(callback: (...args: A) => void, delayMs: number): (...args: A) => void
	{
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () =>
		{
		if (timeoutRef.current)
			{
			clearTimeout(timeoutRef.current);
			}
		}, []);

	return useMemo(() => (...args: A) =>
		{
		if (timeoutRef.current)
			{
			clearTimeout(timeoutRef.current);
			}
		timeoutRef.current = setTimeout(() => callbackRef.current(...args), delayMs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [delayMs]);
	}
